import { installSkyTwinRig } from '../../../packages/domain/src/129-sky-twin-executioners.mjs';
import { installSkyVesperCrystalRig } from '../../../packages/domain/src/125-sky-vesper.mjs';
import { installSkyScrapSupplyRig } from '../../../packages/domain/src/124-sky-scrap-emperor.mjs';
import { installSkyOrchidIrrigationRig } from '../../../packages/domain/src/122-sky-iron-orchid.mjs';
import { installSkyMourningBrideRig } from '../../../packages/domain/src/121-sky-mourning-bride.mjs';
import { installSkyPrismMirrorRig } from '../../../packages/domain/src/120-sky-prism-widow.mjs';
import { SKY_STAGES } from '../../../packages/domain/src/76-sky-campaign.mjs';
import { installSkyAuthoredEnemyRig } from '../../../packages/domain/src/118-sky-authored-enemies.mjs';
import {
  installSkyCinderheartPropRig,
  installSkyCinderheartSlagRig,
} from '../../../packages/domain/src/117-sky-cinderheart.mjs';
import { installSkyAuthoredBossRig } from '../../../packages/domain/src/116-sky-authored-boss.mjs';
import { installSkyCathedralCollision } from '../../../packages/domain/src/114-sky-cathedral-art-geometry.mjs';
import { skyAuthoredBossSpec } from './220-sky-authored-bosses.mjs';
import {
  SKY_LEVIATHAN_ARMOR_PARTS,
  createSkyLeviathanCollision,
  installSkyLeviathanCollision,
} from '../../../packages/domain/src/97-sky-leviathan-armor.mjs';
import { extractSkyRigidGeometry } from './169-sky-rigid-geometry.mjs';
import { skyStageArtVariant } from './134-sky-asset-manifest.mjs';
import { SKY_LEVIATHAN_ASSEMBLY } from '../../../packages/domain/src/95-sky-leviathan-space.mjs';

/** The compact authored pivot/rupture contract is valid only for this exact
 * GLB. Verify bytes before decoding; stale CDN or mismatched exports fail the
 * existing retryable readiness gate instead of changing physical apertures. */
export async function verifySkyLeviathanAsset(bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  if (hash !== SKY_LEVIATHAN_ASSEMBLY.assetSha256)
    throw new Error('Leviathan artwork does not match its physical assembly contract.');
}

/** Extract exact opaque rigid surfaces once per GLTF load. Vertices stay in
 * joint-local metres; hierarchy/rest transforms come from the loaded artwork.
 * Missing, deformed, nonrigid or malformed artwork fails readiness explicitly.
 * The domain compiler owns immutable geometry validation and acceleration. */
export function extractSkyLeviathanCollision(T, scene) {
  const parts = extractSkyRigidGeometry(T, scene, {
    rootName: 'gilded_leviathan',
    parts: SKY_LEVIATHAN_ARMOR_PARTS,
  });
  const handle = createSkyLeviathanCollision(parts);
  if (!handle) throw new Error('Leviathan artwork failed exact collision validation.');
  return handle;
}

/** Bind readiness data to the live run before launch/resume. Future-stage
 * prefetches never mutate the current encounter. A missing required handle is
 * an explicit loading error, so no visible armor can silently become passable. */
export function acceptSkyEncounterData(state, requested, renderer) {
  if (
    state.sector !== requested.sector ||
    skyStageArtVariant(state) !== skyStageArtVariant(requested)
  )
    return;
  const data = renderer.encounterData?.();
  const enemyVariant = SKY_STAGES[state.sector]?.enemyArtVariant;
  if (enemyVariant) {
    const rig = data?.authoredEnemies?.[enemyVariant];
    if (rig?.worldId !== enemyVariant || !installSkyAuthoredEnemyRig(state, rig))
      throw new Error('Authored enemy physical artwork is not ready.');
  }
  const variant = skyStageArtVariant(state);
  if (variant === 'cathedral-of-thunder') {
    if (!installSkyCathedralCollision(state, renderer.encounterData?.()?.cathedral))
      throw new Error('Cathedral physical artwork is not ready.');
    return;
  }
  if (skyAuthoredBossSpec(variant)) {
    const rig = renderer.encounterData?.()?.authoredBosses?.[variant];
    if (rig?.worldId !== variant || !installSkyAuthoredBossRig(state, rig))
      throw new Error('Authored guardian physical artwork is not ready.');
    if (
      variant === 'twin-executioners' &&
      !['executioner-crimson', 'twin-gantry'].every((id) =>
        installSkyTwinRig(state, data?.authoredProps?.[id]),
      )
    )
      throw new Error('Twin aircraft and gantry physical artwork is not ready.');
    if (
      variant === 'scrap-emperor' &&
      !installSkyScrapSupplyRig(state, data?.authoredProps?.['scrap-supplies'])
    )
      throw new Error('Scrap supply physical artwork is not ready.');
    if (
      variant === 'vesper' &&
      !installSkyVesperCrystalRig(state, data?.authoredProps?.['vesper-crystal'])
    )
      throw new Error('Vesper crystal physical artwork is not ready.');
    if (
      variant === 'iron-orchid' &&
      !installSkyOrchidIrrigationRig(state, data?.authoredProps?.['iron-orchid-irrigation'])
    )
      throw new Error('Orchid irrigation artwork is not ready.');
    if (
      variant === 'mourning-bride' &&
      !['mourning-bride-wreck', 'mourning-bride-echo'].every((role) =>
        installSkyMourningBrideRig(state, data?.authoredProps?.[role]),
      )
    )
      throw new Error('Mourning Bride searchlight and echo artwork is not ready.');
    if (
      variant === 'prism-widow' &&
      !installSkyPrismMirrorRig(state, data?.authoredProps?.['prism-mirrors'])
    )
      throw new Error('Prism mirror artwork is not ready.');
    if (
      variant === 'cinderheart' &&
      !installSkyCinderheartPropRig(
        state,
        renderer.encounterData?.()?.authoredProps?.['cinderheart-coolant'],
      )
    )
      throw new Error('Cinderheart coolant artwork is not ready.');
    if (
      variant === 'cinderheart' &&
      !installSkyCinderheartSlagRig(state, data?.authoredProps?.['cinderheart-slag'])
    )
      throw new Error('Cinderheart molten rock artwork is not ready.');
    return;
  }
  if (variant !== 'gilded-leviathan') return;
  const handle = renderer.encounterData?.()?.gildedLeviathan;
  if (handle && state.leviathanCollision === handle) return;
  if (!installSkyLeviathanCollision(state, handle))
    throw new Error('Leviathan physical artwork is not ready.');
}
